import { Input, Typography } from "@material-tailwind/react";
import { InputProps } from "@material-tailwind/react/components/Input";
import React, { ReactElement, useEffect, useImperativeHandle, useRef } from "react";
import { FieldError } from "react-hook-form";
import { useStateful } from "../../../hooks/useStateful";
import { AlertIcon } from "../../Icons/Icons";

export type InputDefaultProps = Omit<
  InputProps,
  "ref" | "crossOrigin" | "error" | "icon" | "defaultValue"
> & {
  id: string;
  label: string;
  defaultValue?: string | number;
  leftIcon?: ReactElement;
  rightIcon?: ReactElement;
  labelIcon?: ReactElement;
  helpText?: string;
  crossOrigin?: "anonymous" | "use-credentials" | "";
  fieldError?: FieldError;
  validationErrorMessage?: string;
  onValueChanged?: (value: string) => void;
  inputRegexPattern?: string;
  inputClasses?: string;
  isOnDarkBg?: boolean;
};

const InputDefault: React.ForwardRefRenderFunction<HTMLInputElement, InputDefaultProps> = (
  {
    id,
    label,
    defaultValue,
    rightIcon,
    leftIcon,
    labelIcon,
    helpText,
    crossOrigin,
    fieldError,
    onValueChanged,
    validationErrorMessage,
    placeholder,
    onBlur,
    onFocus,
    onChange,
    onInput,
    pattern,
    inputRegexPattern,
    inputClasses,
    isOnDarkBg,
    readOnly,
    ...rest
  },
  ref,
) => {
  const _defaultValue = defaultValue !== undefined ? `${defaultValue}` : "";
  const value = useStateful<string>(_defaultValue);
  const focused = useStateful(false);

  const internalRef = useRef<HTMLInputElement>(null);
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    ref,
    () => internalRef.current,
  );

  useEffect(() => {
    if (value.value !== _defaultValue) {
      onValueChanged && onValueChanged(value.value);
    }
  }, [value.value]);

  useEffect(() => {
    if (defaultValue) {
      value.set(_defaultValue);
    }
  }, [defaultValue]);

  const borderColor = focused.value
    ? "border-ldir-blue-900"
    : fieldError != null || validationErrorMessage
      ? "border-ldir-red-900 border-[3px]"
      : isOnDarkBg
        ? "border-ldir-blue-900"
        : "border-ldir-blue-100";

  useEffect(() => {
    //the component renders an empty label that causes issues for screen-readers
    //so we remove it.
    //Consider changing the component
    const labelEl = document.getElementById("remove");
    if (labelEl) {
      labelEl.remove();
    }
  }, []);

  return (
    <div className={`w-full flex ${isOnDarkBg ? "text-ldir-blue-100" : "text-ldir-brown-900"}`}>
      {labelIcon && <div className="w-6 h-6 mr-2 mt-px">{labelIcon}</div>}
      <div className="grow">
        <label htmlFor={id} className={`text-xl mb-px ${fieldError ? "font-bold" : "font-normal"}`}>
          {label}
        </label>
        <div
          className={`flex flex-row items-center min-h-15 border-2 px-4 border-ldir-brown-900 rounded bg-white  ${borderColor} ${inputClasses}`}
        >
          {leftIcon && (
            <button type="button" className="mr-4">
              {leftIcon}
            </button>
          )}
          <Input
            id={id}
            label={undefined}
            data-testid={id}
            inputRef={internalRef}
            labelProps={{ "aria-hidden": true, id: "remove" }}
            aria-describedby={"helptext-" + label}
            crossOrigin={crossOrigin || "anonymous"}
            variant="standard"
            className="text-xl text-ldir-brown-900 pt-4 pb-4 "
            error={fieldError != null}
            readOnly={readOnly}
            {...rest}
            value={value.value}
            onBlur={(e) => {
              focused.set(false);
              onBlur && onBlur(e);
            }}
            onFocus={(e) => {
              focused.set(true);
              onFocus && onFocus(e);
            }}
            onChange={(e) => {
              value.set(e.target.value);
              onChange && onChange(e);
            }}
            onInput={(e) => {
              onInput && onInput(e);
            }}
            placeholder={placeholder}
          />
          {rightIcon}
          <div>{fieldError && !validationErrorMessage && <AlertIcon />}</div>
        </div>
        {fieldError || validationErrorMessage ? (
          <div>
            <Typography
              id={"helptext-" + label}
              className={"text-ldir-red-900 text-lg"}
              placeholder={placeholder}
            >
              {fieldError?.message || validationErrorMessage}
            </Typography>
          </div>
        ) : (
          helpText && (
            <Typography id={"helptext-" + label} placeholder={placeholder} className="text-lg">
              {helpText}
            </Typography>
          )
        )}
      </div>
    </div>
  );
};

export default React.forwardRef(InputDefault);
